import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import useTranslations from 'hooks/useTranslations'
import CloseIcon from 'assets/menu-close.svg'
import useGallery from '../hooks/useGallery'
import ProductImageWithZoom from 'theme/templates/PDP/Gallery/GalleryLightbox/ProductImageWithZoom'
import TouchIcon from 'assets/touch.svg'
import ArrowLeftIcon from 'assets/arrow-button-left.svg'
import ArrowRightIcon from 'assets/arrow-button-right.svg'
import { useDisplayVariant } from 'modules/productDetail'
import WistiaPlayer from 'theme/atoms/WistiaPlayer'
import ThumbnailBar from '../ThumbnailBar'
import useTouchSlide from '../hooks/useTouchSlide'

type Props = {
  onClose: () => void
  'data-cy-state'?: string
}

export default function GalleryLightbox(props: Props) {
  const t = useTranslations<'templates-PDP'>()
  const variant = useDisplayVariant()
  const gallery = useGallery()
  const { holder } = useTouchSlide(gallery.index, gallery.setIndex)
  const handleClose = () => {
    document.body.classList.remove('scrolllock')
    props.onClose()
  }

  React.useEffect(() => {
    document.body.classList.add('scrolllock')
  }, [])

  return (
    <Wrapper data-cy-collection="GalleryLightbox">
      <div className="content">
        <div className="left">
          <ThumbnailBar gallery={gallery}></ThumbnailBar>
        </div>
        <div className="right">
          <MainImage
            $isVideo={gallery.mainImage.type === 'video'}
            className="main-image"
            ref={holder}
          >
            <div className="main-inner">
              {gallery.mainImage.type === 'video' ? (
                <WistiaPlayer
                  key={gallery.mainImage.wistiaID || ''}
                  link={gallery.mainImage.wistiaID || ''}
                  data-cy-state="wistia-player"
                  autoPlay={false}
                  muted={false}
                  controlsVisibleOnLoad={true}
                  settingsControl={true}
                  volumeControl={true}
                  fullscreenButton={true}
                  playbar={true}
                  smallPlayButton={true}
                  playButton={true}
                />
              ) : (
                <ProductImageWithZoom
                  img={gallery.mainImage}
                  alt={variant.data.title}
                  size="l"
                  holderRef={holder}
                  data-cy-state="image-zoom"
                  data-cy-handle-zoom-in="image-zoom-in"
                  data-cy-handle-zoom-reset="image-zoom-reset"
                  data-scroll-id="image-zoom"
                />
              )}
            </div>
          </MainImage>
          <div className="toolbar">
            <div className="zoom-info">
              <TouchIcon />
              <div>{t('gallery_lightbox_zoom')}</div>
            </div>
            <div className="navigation">
              {gallery.totalThumbs > 1 && (
                <button
                  className="previous-button"
                  title={t.asText(false, 'gallery_lightbox_previous')}
                  data-cy-state="show-navigation-prev"
                  data-cy-handle="navigation-prev-click"
                  onClick={() => gallery.setIndex(gallery.index - 1)}
                  tabIndex={0}
                >
                  <ArrowLeftIcon />
                </button>
              )}
              <div className="indicator">
                {t('gallery_lightbox_indicator', {
                  current: gallery.index + 1,
                  total: gallery.totalThumbs
                })}
              </div>
              {gallery.totalThumbs > 1 && (
                <button
                  className="next-button"
                  title={t.asText(false, 'gallery_lightbox_next')}
                  data-cy-state="show-navigation-next"
                  data-cy-handle="navigation-next-click"
                  onClick={() => gallery.setIndex(gallery.index + 1)}
                  tabIndex={0}
                >
                  <ArrowRightIcon />
                </button>
              )}
            </div>
          </div>
        </div>
        <div
          className="close-container"
          onClick={() => handleClose()}
          data-cy-handle="close-drawer-button"
        >
          <CloseIcon />
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  position: fixed;
  right: 0;
  bottom: 0;
  top: 0;
  left: 0;
  z-index: ${theme.zIndex.theme_templates_pdp_gallery_gallerylightbox};
  background: ${theme.colors.white};
  padding: ${theme.spacing('l')};

  > .content {
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing('m')};
    height: 100%;
    > .left {
      display: none;
      @media (min-width: ${theme.ms.LG}px) {
        display: flex;
        flex: 0 0 5.125rem;
      }
      > .thumbnails {
        padding: 0.125rem;
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing('xs')};
        overflow-y: scroll;
        overflow-x: hidden;
        &::-webkit-scrollbar {
          display: none; /* Chrome, Safari and Opera */
        }
        -ms-overflow-style: none; /* IE and Edge */
        scrollbar-width: none; /* Firefox */
      }
    }
    > .right {
      &::-webkit-scrollbar {
        display: none; /* Chrome, Safari and Opera */
      }
      -ms-overflow-style: none; /* IE and Edge */
      scrollbar-width: none; /* Firefox */
      overflow-y: scroll;
      gap: ${theme.spacing('m')};
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: center;
      flex-grow: 1;
      > .toolbar {
        display: flex;
        flex-direction: column;
        text-align: center;
        row-gap: ${theme.spacing('m')};

        > .zoom-info {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${theme.spacing('xxs')};
          ${theme.ty('rc-s')}
          > svg {
            width: 1.5rem;
            height: 1.5rem;
          }

          display: none;
          @media (min-width: ${theme.ms.LG}px) {
            display: block;
          }
        }
        > .navigation {
          ${theme.ty('rc-base')}
          display: flex;
          align-items: center;
          justify-content: center;
          padding-bottom: ${theme.spacing('xxs')};
          gap: ${theme.spacing('l')};

          > .previous-button,
          > .next-button {
            cursor: pointer;
            justify-content: center;
            display: flex;
            align-items: center;
            width: 3.125rem;
            height: 3.125rem;
            border-radius: 0.3125rem;
            border: solid 1px ${theme.colors.shade.b2};
            background-color: ${theme.colors.white};
            box-shadow:
              0px 1px 8px 0px rgba(0, 0, 0, 0.04),
              0px 3px 4px 0px rgba(0, 0, 0, 0.05),
              0px 3px 3px -2px rgba(0, 0, 0, 0.08);
            @media (min-width: ${theme.ms.LG}px) {
              display: none;
            }
            > svg {
              width: 1rem;
              height: 1rem;
              g {
                stroke: ${theme.colors.shade.b2};
              }
            }
          }
        }
      }
    }

    > .close-container {
      position: fixed;
      top: ${theme.spacing('m')};
      right: ${theme.spacing('m')};
      cursor: pointer;
    }
  }
`

const MainImage = styled.div<{ $isVideo: boolean }>`
  flex-grow: 1;
  display: flex;
  justify-content: center;
  ${(p) => p.$isVideo && 'width: 100%'};
  > .main-inner {
    align-self: center;
    max-width: 820px;
    ${(p) => p.$isVideo && 'width: 100%'};
    > img {
      width: 100%;
    }
  }
`
